import { format } from 'date-fns'
import { Box, Card, Divider, Flex, Heading, Text } from '@applyboard/crystal-ui'
import { ReviewRequirementsButton } from './ReviewRequirementsButton'
import { getApplicationFeeAmount, hasApplicationFee } from '../../../utils'
import { StudentApplication } from '../types'
import { AlternateProgramSelected } from '@backend/resources/application'

type DesktopHeaderProps = Readonly<{
  application: StudentApplication
  showProgramName?: boolean
}>

export function DesktopHeader({ application, showProgramName = true }: DesktopHeaderProps) {
  const program = application?.attributes?.programSelected
  const alternatePrograms = application?.attributes?.alternateProgramSelected ?? []
  const applicationFeeAmount = getApplicationFeeAmount(application)

  return (
    <Card emphasis="highlighted" aria-label="Application Information">
      <Flex direction="column" gap={2}>
        {showProgramName ? (
          <Flex pb={4}>
            <Heading level={2} variant="titleS">
              {program?.program?.name}
            </Heading>
          </Flex>
        ) : null}
        <Flex gap={2}>
          <Text variant="labelS">Intake:</Text>
          <Text variant="bodyS">{program?.programIntakeTerm?.name ?? '--'}</Text>
        </Flex>
        <Flex gap={2}>
          <Text variant="labelS">Campus:</Text>
          <Text variant="bodyS">{program?.campus?.name ?? '--'}</Text>
        </Flex>
        <Flex gap={2}>
          <Text variant="labelS">Application ID:</Text>
          <Text variant="bodyS">{application?.attributes?.humanReadableId ?? '--'}</Text>
        </Flex>
        <Flex gap={2}>
          <Text variant="labelS">Application fee:</Text>
          {!application?.id ? (
            <Text variant="bodyS">To be determined</Text>
          ) : hasApplicationFee(application) ? (
            <Text variant="bodyS">
              {Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: application?.attributes?.applicationFee?.currency || 'CAD',
              }).format(applicationFeeAmount)}
            </Text>
          ) : (
            <Text variant="bodyS">No Fee</Text>
          )}
        </Flex>

        {application?.attributes?.applicationInformation?.submittedAt ? (
          <Flex gap={2}>
            <Text variant="labelS">Submission Date:</Text>
            <Text variant="bodyS">
              {format(
                new Date(application.attributes.applicationInformation.submittedAt),
                'MMMM d, yyyy',
              )}
            </Text>
          </Flex>
        ) : null}

        <Flex>
          <ReviewRequirementsButton programId={program?.program?.id as string} />
        </Flex>

        {alternatePrograms.length > 0 ? alternatePrograms.map((alternateProgram) => {
          const key = crypto.randomUUID()
          return <AlternateProgram key={key} alternateProgram={alternateProgram} />
        }) : null}
      </Flex>
    </Card>
  )
}

type AlternateProgramProps = Readonly<{
  alternateProgram: AlternateProgramSelected
}>

function AlternateProgram({ alternateProgram }: AlternateProgramProps) {
  return (
    <>
      <Flex pt={2} pb={4}>
        <Divider m={0} />
      </Flex>

      <Flex>
        <Text variant="labelS">Alternate Program</Text>
      </Flex>
      <Box>
        <Text variant="labelS" as="span">Program name:</Text> <Text variant="bodyS" as="span">{alternateProgram?.program?.name ?? '--'}</Text>
      </Box>
      <Box>
        <Text variant="labelS" as="span">Intake:</Text> <Text variant="bodyS" as="span">{alternateProgram?.programIntakeTerm?.name ?? '--'}</Text>
      </Box>
      <Box>
        <Text variant="labelS" as="span">Campus:</Text> <Text variant="bodyS" as="span">{alternateProgram?.campus?.name ?? '--'}</Text>
      </Box>
    </>
  )
}
